export default {
  label: "United",
  script: `import { createSession } from "@plutoxyz/automation";
import { chromium } from "playwright-core";

/**
 * Prompts the user for United credentials, potentially re-prompting if errors are provided.
 * @param {import("@plutoxyz/automation").Session} session - The Pluto session object.
 * @param {string} [usernameError] - An error message to display for the username field.
 * @param {string} [passwordError] - An error message to display for the password field.
 * @returns {Promise<string[]>} A promise resolving to an array containing the entered credentials (username, password, or just one if re-prompting).
 */
async function promptForCredentials(session, usernameError, passwordError) {
  const userPrompt = {
    label: "Email or MileagePlus® number",
    type: "text",
    attributes: {
      min_length: 3,
      placeholder: "Email or MileagePlus® number",
    },
    error: usernameError,
  };

  const passwordPrompt = {
    label: "Password",
    type: "password",
    attributes: {},
    error: passwordError,
  };

  const prompts = [];
  if (!usernameError && !passwordError) {
    prompts.push(userPrompt, passwordPrompt);
  } else if (usernameError) {
    prompts.push(userPrompt);
  } else if (passwordError) {
    prompts.push(passwordPrompt);
  }

  return await session.prompt({
    title: "Login to United",
    icon: "united.com",
    description: "Enter your United credentials to link your account",
    prompts,
  });
}

/**
 * Handles the main user login flow, including navigation and calling username/password handlers.
 * @param {import("patchright").Page} page - The Playwright Page object.
 * @param {import("@plutoxyz/automation").Session} session - The Pluto session object.
 * @param {string} username - The username (Email or MileagePlus number) to enter.
 * @param {string} passwordInput - The password to enter.
 * @returns {Promise<void>}
 */
async function handleUserLogin(page, session, username, passwordInput) {
  await handleUsername(page, session, username);
  await handlePassword(page, session, passwordInput);
}

/**
 * Enters the username and handles potential validation errors, re-prompting if necessary.
 * @param {import("patchright").Page} page - The Playwright Page object.
 * @param {import("@plutoxyz/automation").Session} session - The Pluto session object.
 * @param {string} username - The username to enter.
 * @returns {Promise<void>}
 */
async function handleUsername(page, session, username) {
  const usernameTextbox = page.getByRole("textbox", {
    name: "Email or MileagePlus® number",
  });
  await usernameTextbox.waitFor({ state: "visible" });
  await usernameTextbox.fill(username);

  await page.keyboard.press("Enter");
  await page.waitForTimeout(1000);

  const errorText = page.getByText(
    /Enter a valid MileagePlus number|No account found|Use your MileagePlus number|Enter letters A/,
  );
  const hasError = (await errorText.allTextContents())[0];

  if (hasError) {
    const [username2] = await promptForCredentials(
      session,
      hasError.replaceAll("error", "").trim() ||
        "Enter a valid MileagePlus number.",
      undefined,
    );
    await handleUsername(page, session, username2);
  }
}

/**
 * Enters the password and handles potential validation errors, re-prompting if necessary.
 * @param {import("patchright").Page} page - The Playwright Page object.
 * @param {import("@plutoxyz/automation").Session} session - The Pluto session object.
 * @param {string} password - The password to enter.
 * @returns {Promise<void>}
 */
async function handlePassword(page, session, password) {
  const passwordTextbox = page.getByRole("textbox", { name: "Password" });
  await passwordTextbox.waitFor({ state: "visible" });
  await passwordTextbox.fill(password);

  await page.keyboard.press("Enter");
  await page.waitForTimeout(1000);

  const errorText = page.getByText(
    /(The account information entered is invalid|Enter a valid password)/,
  );
  const hasError = (await errorText.allTextContents())[0];

  if (hasError) {
    const [password2] = await promptForCredentials(
      session,
      undefined,
      hasError.replaceAll("error", "").trim() || "Enter a valid password.",
    );
    await handlePassword(page, session, password2);
  }
}

/**
 * Handles the 2FA (OTP) verification step if prompted.
 * @param {import("patchright").Page} page - The Playwright Page object.
 * @param {import("@plutoxyz/automation").Session} session - The Pluto session object.
 * @returns {Promise<void>}
 */
async function handle2FA(page, session) {
  const twofaLocator = page.getByText("Enter code");
  try {
    await twofaLocator.waitFor({ state: "attached", timeout: 5000 });
    console.log("2FA prompt detected.");

    // Double-check visibility just in case waitFor('attached') was brief
    if ((await twofaLocator.elementHandles()).length > 0) {
      console.log("Verification code prompt is visible.");
      // Attempt to get title text from an alert (may need adjustment based on actual site behavior)
      let promptTitle = "Enter Verification Code"; // Default title
      try {
        const alertElement = page.getByRole("alert").first(); // Use first() to avoid strict mode errors if multiple alerts exist
        if (await alertElement.isVisible({ timeout: 2000 })) {
          promptTitle = await alertElement.innerText();
        } else {
          console.log("No alert found for 2FA title, using default.");
        }
      } catch (alertError) {
        console.warn(
          \`Could not get alert text for 2FA title: \${alertError.message}, using default.\`,
        );
      }

      const [otpCode] = await session.prompt({
        title: promptTitle,
        description: "Please enter the 6-digit code.",
        icon: "united.com",
        prompts: [
          {
            label: "Code",
            type: "number_code",
            attributes: {
              length: 6,
            },
          },
        ],
      });
      console.log("OTP Code received from user.");

      const otpInputs = page.locator(".atm-c-otp-input");
      for (let i = 0; i < 6; i++) {
        await otpInputs.nth(i).fill(otpCode[i]);
      }

      await page.keyboard.press("Enter");
      console.log("OTP Submitted.");
    } else {
      console.log("2FA prompt was attached but is no longer visible.");
    }
  } catch (error) {
    // Timeout or other error waiting for 2FA prompt
    console.log("No 2FA prompt detected within timeout or error occurred.");
    // Proceed assuming 2FA was not required or login failed before this point
  }
}

/**
 * Extracts MileagePlus data after successful login.
 * @param {import("patchright").Page} page - The Playwright Page object.
 * @returns {Promise<object[]>} A promise resolving to an array of data objects.
 */
async function extractData(page) {
  await page.waitForURL("https://www.united.com/en/us");
  console.log("Navigated to post-login page.");

  const loginBtnLocator = page.locator("#loginButton");
  await loginBtnLocator.waitFor({ state: "visible" });
  await loginBtnLocator.click();
  console.log("Clicked login button to open user menu/sidebar.");

  const signOut = page.getByRole("button", { name: "SIGN OUT" });
  await signOut.waitFor({ state: "visible" });
  console.log("User menu/sidebar is visible (SIGN OUT button found).");

  const sidebarText = page.locator(".atm-c-drawer__body");
  await sidebarText.waitFor({ state: "visible" });

  const sidebarTextContent = await sidebarText.innerText();
  const splitText = sidebarTextContent.split("\\n\\n");
  console.log(\`Extracted sidebar text, found \${splitText.length} parts.\`);

  const keys = [
    { key: "MILEAGEPLUS NUMBER", label: "MileagePlusNumber" },
    { key: "MILES", label: "Miles" },
    { key: "PQF", label: "premierQualifyingFlights" },
    { key: "PQP", label: "premierQualifyingPoints" },
  ];

  const data = [];
  splitText.forEach((text, i) => {
    const key = keys.find((k) => text.includes(k.key));
    if (key) {
      if (key.label === "MileagePlusNumber") {
        const value = text.split(":")[1]?.trim();
        if (value) {
          data.push({ [key.label]: value });
          console.log(\`  Found \${key.label}: \${value}\`);
        }
      } else {
        const val = splitText[i + 1];
        if (val) {
          data.push({ [key.label]: val });
          console.log(\`  Found \${key.label}: \${val}\`);
        }
      }
    }
  });
  console.log(\`Finished parsing sidebar data. Found \${data.length} items.\`);
  return data;
}

// --- Main script execution ---

const session = await createSession();
const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = context.pages()[0];
const urlPromise = page.goto("https://www.united.com/en/us/united-mileageplus-signin", {
  waitUntil: "domcontentloaded",
});

const credentialsPromise = promptForCredentials(session, undefined, undefined);

const [, [username, password]] = await Promise.all([
  urlPromise,
  credentialsPromise,
]);

await handleUserLogin(page, session, username, password);
await handle2FA(page, session);

const data = await extractData(page);
await session.prove("UnitedMileagePlusNumber", data);

await page.close();
await context.close();
await browser.close();
`,
};
